/**
 * CompatibilityBadge — [T161]
 *
 * Amber badge that surfaces COMPAT_* backend errors with a one-click fix
 * affordance. Rendered in the WidgetFrame error overlay when a widget
 * returns `status: 'incompatible'` with a structured COMPAT_* error code.
 *
 * @see https://react.dev/reference/react — React component patterns
 * @see https://tailwindcss.com/docs/background-color — amber palette tokens
 */
import type { ReactNode } from 'react';
import { AlertTriangle, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Structured compatibility error returned by the backend for incompatible widgets. */
export interface CompatibilityError {
  code: string;
  message?: string;
  /** Minimum required columns (grid units). Present when code === 'COMPAT_SIZE_TOO_SMALL'. */
  minW?: number;
  /** Minimum required rows (grid units). Present when code === 'COMPAT_SIZE_TOO_SMALL'. */
  minH?: number;
  /**
   * [T149-CONT-BE] Present only on DESCRIPTOR_SPOOF_MISMATCH.
   * The canonical descriptor key of the persisted widget record in the database.
   * Use this to retry the data fetch with the correct descriptor key.
   */
  persistedCanonicalKey?: string | null;
  /**
   * [T149-CONT-BE] Present only on DESCRIPTOR_SPOOF_MISMATCH.
   * The canonical descriptor key that the backend resolved from the client's request.
   */
  requestedCanonicalKey?: string | null;
}

export interface CompatibilityBadgeProps {
  widgetId: string;
  error: CompatibilityError;
  onRequestFix: (opts: { widgetId: string; targetCols?: number; targetRows?: number }) => void;
  className?: string;
}

type BadgeVariant = {
  icon: ReactNode;
  label: string;
  fixLabel?: string;
  buildPayload?: (
    error: CompatibilityError,
    widgetId: string,
  ) => { widgetId: string; targetCols?: number; targetRows?: number };
};

function resolveVariant(code: string): BadgeVariant | null {
  switch (code) {
    case 'COMPAT_SIZE_TOO_SMALL':
      return {
        icon: <Maximize2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
        label: 'Too small — resize to fit',
        fixLabel: 'Resize',
        buildPayload: (error, widgetId) => ({
          widgetId,
          targetCols: error.minW,
          targetRows: error.minH,
        }),
      };
    case 'COMPAT_DESCRIPTOR_MISMATCH':
      return {
        icon: <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
        label: 'Metric not available for this widget type',
        fixLabel: 'Reconfigure',
        buildPayload: (_error, widgetId) => ({ widgetId }),
      };
    case 'COMPAT_VISUAL_TYPE_UNSUPPORTED':
      return {
        icon: <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
        label: 'Widget type not supported',
        fixLabel: 'Replace',
        buildPayload: (_error, widgetId) => ({ widgetId }),
      };
    case 'DESCRIPTOR_SPOOF_MISMATCH':
      return {
        icon: <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />,
        label: 'Widget setup changed — auto-retrying',
        fixLabel: 'Reconfigure',
        buildPayload: (_error, widgetId) => ({ widgetId }),
      };
    default:
      return null;
  }
}

/**
 * Amber compatibility badge. Renders a human-readable explanation of a
 * COMPAT_* incompatibility and a one-click fix button when applicable.
 *
 * For unknown codes the error message is shown as-is with no fix affordance.
 */
export function CompatibilityBadge({
  widgetId,
  error,
  onRequestFix,
  className,
}: CompatibilityBadgeProps) {
  const variant = resolveVariant(error.code);

  const handleFix = () => {
    if (!variant?.buildPayload) return;
    onRequestFix(variant.buildPayload(error, widgetId));
  };

  const label = variant?.label ?? (error.message ?? 'Incompatible configuration');
  const icon = variant?.icon ?? <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />;

  return (
    <div
      data-testid={`compat-badge-${widgetId}`}
      className={cn(
        'inline-flex items-center gap-2 rounded-md border px-2 py-1',
        'bg-amber-100 border-amber-400 text-amber-800',
        className,
      )}
    >
      {icon}
      <span className="text-xs break-words">{label}</span>
      {variant?.fixLabel && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          data-testid="compat-warning-fix-btn"
          onClick={handleFix}
          className="min-h-[44px] border-amber-400/80 bg-amber-50 text-amber-900 hover:bg-amber-100"
        >
          {variant.fixLabel}
        </Button>
      )}
    </div>
  );
}

export default CompatibilityBadge;